/**
 * Goal Game — the physical "goal card" page.
 *
 * A tap on an NFC card opens /goal-trigger/{token}: it fires the goal on load
 * (POST, same fire-only token), shows a full-screen red GOAL! with the existing
 * beacon, then settles into "Goal is live. Tap the Molson card."
 *
 * The horn is attempted immediately; iOS/Safari blocks sound without a gesture,
 * so a blocked attempt arms a one-shot listener and the first touch plays it.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoalBeacon } from '../../components/goalgame/GoalBeacon';
import { playHorn } from '../../components/goalgame/goalHorn';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-game`;
const FLASH_MS = 1800;

export default function GoalTrigger() {
    const { token } = useParams();
    const { t } = useTranslation();
    const [phase, setPhase] = useState('firing');   // firing | live | failed
    const [needsTouch, setNeedsTouch] = useState(false);
    const [orgName, setOrgName] = useState('');
    const fired = useRef(false);

    useEffect(() => {
        if (fired.current) return;
        fired.current = true;

        playHorn().catch(() => setNeedsTouch(true));

        (async () => {
            let ok = false;
            try {
                const res = await fetch(`${API}/trigger/${token}`, { method: 'POST' });
                ok = res.status === 204;
            } catch (_e) { ok = false; }
            setTimeout(() => setPhase(ok ? 'live' : 'failed'), FLASH_MS);
        })();

        // The card's own name, so no brand is hardcoded on this page.
        (async () => {
            try {
                const res = await fetch(`${API}/trigger/${token}/org`);
                if (res.ok) setOrgName(((await res.json()).organization_name || '').trim());
            } catch (_e) { /* the neutral fallback is used */ }
        })();
    }, [token]);

    // One-shot: the first touch anywhere plays the horn the browser refused.
    useEffect(() => {
        if (!needsTouch) return;
        const go = () => { playHorn().catch(() => {}); setNeedsTouch(false); };
        window.addEventListener('pointerdown', go, { once: true });
        return () => window.removeEventListener('pointerdown', go);
    }, [needsTouch]);

    if (phase === 'firing') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#1a0404] px-6"
                 data-testid="goal-trigger-flash">
                <GoalBeacon />
                <p className="text-6xl font-black tracking-tight text-red-500 mt-4"
                   data-testid="goal-trigger-banner">GOAL!</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#0b0b0f] px-6 text-center"
             data-testid={phase === 'live' ? 'goal-trigger-live' : 'goal-trigger-failed'}>
            {phase === 'live' ? (
                <>
                    <p className="text-2xl font-bold text-white">Goal is live.</p>
                    <p className="text-base text-white/60 mt-2" data-testid="goal-trigger-tap-hint">
                        {orgName
                            ? t('goalGame.tapCardNamed', { name: orgName })
                            : t('goalGame.tapCardFallback')}
                    </p>
                </>
            ) : (
                <p className="text-base text-white/70" data-testid="goal-trigger-error">
                    This goal card is no longer active.
                </p>
            )}
            {needsTouch && (
                <p className="mt-8 text-xs uppercase tracking-widest text-white/35"
                   data-testid="goal-trigger-touch-hint">Touch the screen for sound</p>
            )}
        </div>
    );
}
